import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import useTomoUserInfo from './hooks/useTomoUserInfo';
import { UserType } from './types/types';
import { useInitData, useWebApp } from '@vkruglikov/react-telegram-web-app';
import { InitDataUnsafe } from '@vkruglikov/react-telegram-web-app/lib/useInitData';
import {
  api,
  getTelegramUserInfoApi,
  loginApi,
  sendBindEmailCodeApi,
  verifyBindEmailCodeApi,
} from './api';
import useStore from './state/useStore';
import useCheckLoginByCode from './hooks/api/useCheckLoginByCode';
import { generateRandomString, hashWithWebCrypto } from './util';
import { CONNECT_MAP } from './index';
import * as apiList from './api';
import TomoModal from './components/TomoModal';
import { TomoOptions, SupportedProvider } from './v2/types/types';
import type { TonProvider } from './v2/provider/TonProvider/TonProvider';
import type { EthereumProvider } from './v2/provider/EthereumProvider/EthereumProvider';
import type SolanaProvider from './v2/provider/SolanaProvider/SolanaProvider';
import type SuiProvider from './v2/provider/SuiProvider/SuiProvider';

type EnvType = 'dev' | 'test' | 'main';
type LoginType = 'tomo' | 'telegram'; //auth=use Tomo auth,token = use Telegram auth
export type TomoContextType = {
  env: EnvType;
  tmaid: string;
  tmakey: string;
  endpoints: [string];
  onLogin: (loginType?: LoginType, open?: boolean) => Promise<any>;
  userInfo: UserType | null;
  onLogout: () => void;
  telegramData: InitDataUnsafe;
  tMAuthLink: string;
  // TODO delete
  onSendEmailCode: (params: { email: string }) => Promise<any>;
  // TODO delete
  onVerifyEmailCode: (params: {
    email: string;
    code: string;
    tradePassword: string;
  }) => Promise<any>;
  // TODO delete
  onUpdateUserInfo: () => Promise<any>;
  // TODO delete
  apiList: typeof apiList;
  openConnectModal: (options?: ConnectOptions) => void;
  closeConnectModal: () => void;
  connected: boolean;
  providers: {
    tomo_ton?: TonProvider;
    ethereum?: EthereumProvider;
    tomo_sol?: SolanaProvider;
    tomo_sui?: SuiProvider;
  };
  setProviders: React.Dispatch<React.SetStateAction<{}>>;
  error?: string;
  connectResult?: ConnectResult;
  supportedProviders: SupportedProvider[];
  isEvmConnected: boolean;
  useEvmChains: number[];
};

export type ConnectResult = {
  connected?: boolean;
  result: { tonProof: Record<string, any> };
};

export type ConnectOptions = {
  tonProof?: string;
  workChain?: number;
};

export const defaultEndpoints: {
  [key in EnvType]: [string];
} = {
  dev: ['https://apis.tomo.inc/'],
  test: ['https://apis.tomo.inc/'],
  main: ['https://apis.tomo.inc/'],
};
export const defaultTMAuthLink = 'https://t.me/tomowalletbot/tomoauthapp';

export const TomoContext = createContext<TomoContextType | undefined>(
  undefined
);

export interface TomoProviderProps {
  children: ReactNode;
  tmaid?: string;
  tmakey?: string;
  env?: EnvType;
  customEndpoints?: [string];
  customTMAuthLink?: string;
  theme?: 'dark' | 'light';
  supportedProviders?: ('EVM' | 'TON' | 'Solana' | 'SUI')[];
  supportedConnects?: CONNECT_MAP[];
  manifestUrl?: string;
  tomoOptions?: TomoOptions;
  useEvmChains?: number[];
  overrideTomoRpcUrls?: Record<number, string>;
}

// function useMemoTomoContextValue(context: TomoContextType) {
//   const values = Object.values(context)
//   return useMemo(() => context, values)
// }

export const TomoProvider: React.FC<TomoProviderProps> = ({
  children,
  tmaid,
  tmakey,
  env = 'dev',
  customEndpoints,
  customTMAuthLink,
  theme = 'light',
  supportedProviders = ['EVM'],
  supportedConnects = [],
  manifestUrl,
  tomoOptions,
  useEvmChains = [1],
  overrideTomoRpcUrls,
}) => {
  const [code, setCode] = useState('');
  const { data, isLoading } = useCheckLoginByCode(code);
  const webApp = useWebApp();
  const { setPaymentPwdExists, paymentPwdExists } = useStore();
  // const { biometryManager } = useBiometricManager();
  const [opened, setOpened] = useState(false);
  const [providers, setProviders] = useState<TomoContextType['providers']>({});
  const [isEvmConnected, setIsEvmConnected] = useState(false);

  const [initDataUnsafe, initData] = useInitData();
  const {
    setUserInfo,
    userInfo,
    // deviceId,
    // generateDeviceId,
  } = useTomoUserInfo();
  const [connected, setConnected] = useState(false);
  const [connectOptions, setConnectOptions] = useState<ConnectOptions | null>();
  const [connectResult, setConnectResult] = useState<ConnectResult>(null);

  const endpoints = customEndpoints || defaultEndpoints[env];
  const tMAuthLink = customTMAuthLink || defaultTMAuthLink;
  useEffect(() => {
    api.init(endpoints, tmaid, tmakey);
  }, [endpoints, tmaid, tmakey]);

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('tm-dark');
    } else {
      document.documentElement.classList.remove('tm-dark');
    }
  }, [theme]);

  const onLogin = useCallback(async (type: LoginType = 'tomo', open = true) => {
    if (type == 'tomo') {
      const c = generateRandomString(16);
      console.log('code', c);
      setCode(c);
      if (open) webApp?.openTelegramLink(tMAuthLink + '?startapp=' + c);
      return tMAuthLink + '?startapp=' + c;
    }

    if (!initData) {
      throw new Error('Please open the telegram and try again');
    }
    const res = await loginApi(`${initData}`);
    setUserInfo(res);
    // if (!deviceId && !biometryManager.isBiometricTokenSaved) {
    //   try {
    //     await generateDeviceId();
    //   } catch (err) {
    //     console.error('generateDeviceId', err);
    //     // setUserInfo(null);
    //     throw err;
    //   }
    // }

    return res;
  }, []);
  useEffect(() => {
    (async () => {
      if (code && data) {
        setUserInfo(data);

        // if (!deviceId && !biometryManager.isBiometricTokenSaved) {
        //   try {
        //     await generateDeviceId();
        //   } catch (err) {
        //     console.error('generateDeviceId', err);
        //     // setUserInfo(null);
        //     throw err;
        //   }
        // }
      }
    })();
  }, [data, code]);

  const onLogout = useCallback(() => {
    setUserInfo(null);
    setPaymentPwdExists(-1);
  }, []);
  const onSendEmailCode = useCallback(async (params: { email: string }) => {
    return await sendBindEmailCodeApi(params);
  }, []);
  const onVerifyEmailCode = useCallback(
    async (params: { email: string; code: string; tradePassword: string }) => {
      if (params.tradePassword.length < 6) {
        throw new Error('Trade password must be at least 6 characters');
      }
      params.tradePassword = await hashWithWebCrypto(params.tradePassword);
      return await verifyBindEmailCodeApi(params);
    },
    []
  );
  const onUpdateUserInfo = useCallback(async () => {
    const res = await getTelegramUserInfoApi();
    setUserInfo({
      ...userInfo,
      ...res.result,
    });
    return res.result;
  }, []);

  const openConnectModal = useCallback((options?: ConnectOptions) => {
    setConnectOptions(options);
    setOpened(true);
  }, []);

  const closeConnectModal = useCallback(() => {
    setOpened(false);
  }, []);

  // evm connection
  // useEffect(() => {
  //   if (providers.ethereum) {
  //     console.log({ ethereum: providers.ethereum });
  //     const isConnected = providers.ethereum.isConnected;
  //     console.log({ isConnected });
  //     setIsEvmConnected(isConnected);
  //     if (isConnected) {
  //       providers.ethereum.request({
  //         method: 'eth_requestAccounts',
  //         params: [],
  //       }).then((res) => {
  //         debugger
  //       });
  //     }
  //   }
  // }, [providers.ethereum]);

  const tomoProviderValue: TomoContextType = useMemo(
    () => ({
      tmaid,
      tmakey,
      env,
      endpoints,
      userInfo,
      onLogin,
      onLogout,
      telegramData: initDataUnsafe,
      tMAuthLink,
      onSendEmailCode,
      onVerifyEmailCode,
      onUpdateUserInfo,
      apiList,
      openConnectModal,
      closeConnectModal,
      connected,
      providers,
      setProviders,
      connectResult,
      supportedProviders,
      isEvmConnected,
      useEvmChains,
      overrideTomoRpcUrls,
      ...(supportedProviders.includes('TON') && !manifestUrl
        ? { error: 'TON Connect need manifestUrl!' }
        : {}),
    }),
    [
      tmaid,
      tmakey,
      env,
      endpoints,
      userInfo,
      onLogin,
      onLogout,
      initDataUnsafe,
      tMAuthLink,
      onSendEmailCode,
      onVerifyEmailCode,
      onUpdateUserInfo,
      openConnectModal,
      closeConnectModal,
      connected,
      providers,
      connectResult,
      supportedProviders,
      isEvmConnected,
      manifestUrl,
      useEvmChains,
      overrideTomoRpcUrls,
    ]
  );

  return (
    <TomoContext.Provider value={tomoProviderValue}>
      {children}
      <TomoModal
        opened={opened}
        onClose={closeConnectModal}
        title="Log in or sign up"
        manifestUrl={manifestUrl || './manifestUrl.json'}
        close
        setConnected={setConnected}
        supportedProviders={supportedProviders}
        supportedConnects={supportedConnects}
        setProviders={setProviders}
        tomoOptions={tomoOptions}
        theme={theme}
        connectOptions={connectOptions}
        setConnectResult={setConnectResult}
      />
    </TomoContext.Provider>
  );
};

export const useTomo = () => {
  const context = useContext(TomoContext);

  if (context === undefined) {
    throw new Error('useTomo must be used within a TomoProvider');
  }
  if (context.error) {
    console.log(context.error);
    throw new Error(context.error);
  }
  return context;
};
